import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react-liveness/styles.css';
import '@aws-amplify/ui-react/styles.css';

// Import from config
import { region, awsConfig } from '../config/aws-config';

// Import custom hooks
import { useLivenessSession } from '../hooks/useLivenessSession';

// Import services
import apiClient, { API_ENDPOINTS } from '../services/api';

// Import UI components
import LoadingSpinner from './ui/LoadingSpinner';
import ErrorMessage from './ui/ErrorMessage';

/**
 * LivenessCheck component handles the face liveness verification process
 */
const LivenessCheck: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const [checkComplete, setCheckComplete] = useState<boolean>(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState<boolean>(false);
  const [detectorKey, setDetectorKey] = useState<number>(0);
  const livenessSessionToken = new URLSearchParams(location.search).get('token');

  // Track mobile-landscape orientation. Amplify's FaceLivenessDetector throws
  // MOBILE_LANDSCAPE_ERROR on rotation; blocking it behind an overlay avoids that state.
  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape) and (max-width: 1024px)');
    const update = () => setIsMobileLandscape(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Remount the detector once the user returns to portrait so its state machine resets.
  useEffect(() => {
    if (!isMobileLandscape) setDetectorKey((k) => k + 1);
  }, [isMobileLandscape]);

  // Use custom hook for session data and API calls
  const {
    sessionDetails,
    loading,
    error,
    resultsLoading,
    fetchSessionResults,
    setErrorMessage
  } = useLivenessSession(sessionId, livenessSessionToken);

  // Set up mutation observer to intercept the Start video check button
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              const startButton = node.querySelector('button.amplify-button--primary') as HTMLButtonElement;
              if (startButton?.textContent?.includes('Start video check')) {
                // Replace the original click handler
                const originalClick = startButton.onclick;
                startButton.onclick = async (e: MouseEvent) => {
                  console.log('Liveness check started');
                  try {
                    // Call the webhook endpoint for start
                    await apiClient.post(API_ENDPOINTS.LIVENESS.WEBHOOK(livenessSessionToken ?? undefined), {
                      sessionId,
                      eventType: 'STARTED'
                    });
                  } catch (err) {
                    console.error('Error calling webhook:', err);
                  }
                  // Call the original click handler
                  if (originalClick) {
                    originalClick.call(startButton, e);
                  }
                };
                // Disconnect once we've found and modified the button
                observer.disconnect();
              }
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup
    return () => observer.disconnect();
  }, [sessionId, livenessSessionToken]);

  /**
   * Handle completion of the liveness analysis
   */
  const handleAnalysisComplete = async (): Promise<void> => {
    console.log('Liveness check completed');
    setCheckComplete(true);

    try {
      // Call the webhook endpoint
      await apiClient.post(API_ENDPOINTS.LIVENESS.WEBHOOK(livenessSessionToken ?? undefined), {
        sessionId,
        eventType: 'COMPLETED'
      });
    } catch (err) {
      console.error('Error calling webhook:', err);
    }

    // Fetch the updated results
    // await fetchSessionResults();

    window.close();
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner
          size="large"
          message="Loading liveness session..."
        />
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <ErrorMessage message={error} />
      </div>
    );
  }

  /**
   * Render results after liveness check is complete
   */
  const renderResults = () => {
    if (resultsLoading) {
      return (
        <div className="flex justify-center items-center min-h-[200px]">
          <LoadingSpinner
            size="medium"
            message="Loading results..."
            className="flex-row items-center"
          />
        </div>
      );
    }

    if (!sessionDetails) {
      return (
        <div className="text-center p-3">
          <h3 className="text-lg font-medium text-red-600">
            Failed to load liveness check results.
          </h3>
        </div>
      );
    }

    return (
      <div className="p-3">
        <div className="text-center mb-2">
          <p className="text-gray-700 mb-6">
            <strong>This page will close automatically.</strong>
            <br />
            If you are not redirected within 10 seconds, select <strong>Close Window</strong> below.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 mr-2"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Main render
   */
  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 items-center justify-center">
            {checkComplete ? (
              renderResults()
            ) : isMobileLandscape ? (
              <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                <svg
                  className="w-16 h-16 text-gray-500 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16V7a3 3 0 013-3h7a3 3 0 013 3v1M4 16a3 3 0 003 3h7a3 3 0 003-3m-13 0l3-3m10 3l-3-3m-7 6v2m4-2v2" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Please rotate your device
                </h2>
                <p className="text-sm text-gray-600">
                  The liveness check needs to run in portrait orientation. Rotate your device upright to continue.
                </p>
              </div>
            ) : (
                <div className="flex justify-center">
                  <FaceLivenessDetector
                    key={detectorKey}
                    sessionId={sessionId ?? ''}
                    region={region}
                    onAnalysisComplete={handleAnalysisComplete}
                    onError={async (livenessError) => {
                      // Amplify's landscape guard — surface a retry overlay instead of a hard error.
                      if ((livenessError as { state?: string })?.state === 'MOBILE_LANDSCAPE_ERROR') {
                        setIsMobileLandscape(true);
                        return;
                      }

                      console.error('Liveness check error:', livenessError);
                      console.error('Error details:', {
                        error: livenessError,
                        sessionId,
                        region,
                        config: awsConfig
                      });

                      try {
                        // Call the webhook endpoint for error
                        await apiClient.post(API_ENDPOINTS.LIVENESS.WEBHOOK(livenessSessionToken ?? undefined), {
                          sessionId,
                          eventType: 'ERROR'
                        });
                      } catch (err) {
                        console.error('Error calling webhook:', err);
                      }

                      setErrorMessage('An error occurred during the liveness check. Please try again.');
                    }}
                  />
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivenessCheck;
